import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "@/lib/api";
import { getActionableError } from "@/lib/errors";
import WorkingHourglass from "@/components/WorkingHourglass";
import ContextScreenshotInput from "@/components/ContextScreenshotInput";
import {
  ArrowClockwise,
  ClockCounterClockwise,
  Copy,
  EnvelopeSimple,
  MagicWand,
  PencilSimple,
  Sparkle,
  Trash,
  WarningCircle,
  X,
} from "@phosphor-icons/react";
import { toast } from "sonner";

const REFINEMENT_OPTIONS = [
  {
    value: "shorter",
    label: "Shorter",
    description: "Remove repetition and secondary detail.",
  },
  {
    value: "direct",
    label: "Direct",
    description: "Clarify the purpose, action, and next step.",
  },
  {
    value: "warmer",
    label: "Warmer",
    description: "Use a more human and relational tone.",
  },
  {
    value: "simpler",
    label: "Simpler",
    description: "Make the language and structure easier to understand.",
  },
  {
    value: "executive",
    label: "Executive",
    description: "Lead with the decision, impact, and next step.",
  },
  {
    value: "diplomatic",
    label: "Diplomatic",
    description: "Handle a sensitive point with calm and tact.",
  },
];

function refinementLabel(value) {
  return REFINEMENT_OPTIONS.find((option) => option.value === value)?.label || "Refined";
}

function savedEmail(value) {
  if (!value || typeof value !== "object") return null;
  const subject = String(value.subject || "").trim();
  const body = String(value.body || "").trim();
  return subject && body ? { ...value, subject, body } : null;
}

function savedVersions(value, activeEmail) {
  const candidates = Array.isArray(value) ? value.map(savedEmail).filter(Boolean) : [];
  const active = savedEmail(activeEmail);
  if (active && !candidates.some((version) => version.version_id === active.version_id)) {
    candidates.push(active);
  }
  return candidates;
}

function versionKindLabel(version) {
  if (version?.version_kind === "refined") return refinementLabel(version.refinement);
  return {
    initial: "First draft",
    regenerated: "Regenerated",
    context: "Added context",
    manual_edit: "Manual edit",
  }[version?.version_kind] || "Saved version";
}

function versionTime(version) {
  const value = version?.version_created_at || version?.updated_at || version?.generated_at;
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(parsed);
}

export default function FollowUpEmail({
  callId,
  customerName = "",
  available = false,
  suggestion = null,
  onSaved,
  onSuggestionDismissed,
  triggerClassName = "follow-up-email-trigger",
  demoEmail = null,
  demoReadOnly = false,
}) {
  const initialDemoEmail = savedEmail(demoEmail);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState(initialDemoEmail);
  const [versions, setVersions] = useState(initialDemoEmail ? [initialDemoEmail] : []);
  const [viewedVersion, setViewedVersion] = useState(null);
  const [knownAvailable, setKnownAvailable] = useState(Boolean(available || initialDemoEmail));
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [subjectDraft, setSubjectDraft] = useState("");
  const [bodyDraft, setBodyDraft] = useState("");
  const [error, setError] = useState("");
  const [confirmCreate, setConfirmCreate] = useState(false);
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [contextEntryOpen, setContextEntryOpen] = useState(false);
  const [contextDraft, setContextDraft] = useState("");
  const [contextError, setContextError] = useState("");
  const [contextImageStatus, setContextImageStatus] = useState("empty");
  const contextImageRef = useRef(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [versionPendingDelete, setVersionPendingDelete] = useState(null);
  const [deletingVersion, setDeletingVersion] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [activeRefinement, setActiveRefinement] = useState("");
  const [dismissingSuggestion, setDismissingSuggestion] = useState(false);
  const [suggestionDismissedLocally, setSuggestionDismissedLocally] = useState(Boolean(suggestion?.dismissed));

  useEffect(() => {
    const seeded = savedEmail(demoEmail);
    if (seeded) {
      setEmail(seeded);
      setVersions([seeded]);
      setViewedVersion(null);
      setKnownAvailable(true);
      setSubjectDraft(seeded.subject);
      setBodyDraft(seeded.body);
      return;
    }
    setKnownAvailable(Boolean(available));
  }, [available, callId, demoEmail]);

  useEffect(() => {
    setSuggestionDismissedLocally(Boolean(suggestion?.dismissed));
  }, [callId, suggestion?.dismissed, suggestion?.recommended]);

  useEffect(() => {
    if ((!open && !confirmCreate) || typeof document === "undefined") return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      if (confirmCreate) {
        setConfirmCreate(false);
        setContextEntryOpen(false);
        setContextDraft("");
        setContextError("");
      }
      else if (contextEntryOpen) {
        setContextEntryOpen(false);
        setContextDraft("");
        setContextError("");
      }
      else if (confirmRegenerate) setConfirmRegenerate(false);
      else if (refineOpen) setRefineOpen(false);
      else if (versionPendingDelete) setVersionPendingDelete(null);
      else if (versionsOpen) setVersionsOpen(false);
      else if (viewedVersion) setViewedVersion(null);
      else setOpen(false);
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", closeOnEscape);
    };
  }, [open, confirmCreate, confirmRegenerate, contextEntryOpen, versionsOpen, viewedVersion, versionPendingDelete, refineOpen]);

  const acceptEmail = (value, versionHistory = null) => {
    const next = savedEmail(value);
    if (!next) return null;
    setEmail(next);
    setVersions(savedVersions(versionHistory, next));
    setViewedVersion(null);
    setKnownAvailable(true);
    setSubjectDraft(next.subject);
    setBodyDraft(next.body);
    onSaved?.(next);
    return next;
  };

  const generate = async (force = false, additionalContext = null, refinement = "") => {
    if (demoReadOnly || !callId || generating) return;
    setError("");
    setEditing(false);
    setActiveRefinement(refinement);
    setGenerating(true);
    try {
      const payload = { force };
      if (typeof additionalContext === "string" && additionalContext.trim()) {
        payload.additional_context = additionalContext.trim();
      }
      if (refinement) payload.refinement = refinement;
      const { data } = await api.post(`/calls/${encodeURIComponent(callId)}/follow-up-email`, payload);
      const next = acceptEmail(data?.follow_up_email, data?.follow_up_email_versions);
      if (!next) throw new Error("Incomplete follow-up email response");
      setContextDraft("");
      toast.success(
        refinement
          ? `${refinementLabel(refinement)} version ready`
          : (force ? "Follow-up email regenerated" : "Follow-up email ready")
      );
    } catch (generationError) {
      setError(getActionableError(
        generationError,
        refinement
          ? "Callsect could not refine this follow-up email. Your current email was not changed."
          : "Callsect could not create the follow-up email. Your existing email was not changed."
      ));
    } finally {
      setGenerating(false);
      setActiveRefinement("");
    }
  };

  const loadExisting = async () => {
    if (demoReadOnly || !callId || loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/calls/${encodeURIComponent(callId)}`);
      const next = acceptEmail(data?.follow_up_email, data?.follow_up_email_versions);
      if (!next) await generate(false);
    } catch (loadError) {
      setError(getActionableError(loadError, "Callsect could not open this follow-up email."));
    } finally {
      setLoading(false);
    }
  };

  const openEmail = () => {
    setError("");
    setEditing(false);
    setConfirmRegenerate(false);
    setContextEntryOpen(false);
    setContextDraft("");
    setContextError("");
    setVersionsOpen(false);
    setViewedVersion(null);
    setVersionPendingDelete(null);
    setRefineOpen(false);
    if (demoReadOnly && savedEmail(demoEmail)) {
      setOpen(true);
      return;
    }
    if (!email && !knownAvailable) {
      setConfirmCreate(true);
      return;
    }
    setOpen(true);
    if (!email) void loadExisting();
  };

  const confirmFirstGeneration = async () => {
    const imageResult = await contextImageRef.current?.prepareSubmission() || { ready: true, context: "" };
    if (!imageResult.ready) return;
    const context = [contextDraft.trim(), imageResult.context].filter(Boolean).join("\n\n");
    setConfirmCreate(false);
    setContextEntryOpen(false);
    setOpen(true);
    void generate(false, context || null);
  };

  const addContext = async () => {
    const imageResult = await contextImageRef.current?.prepareSubmission() || { ready: true, context: "" };
    if (!imageResult.ready) return;
    const context = [contextDraft.trim(), imageResult.context].filter(Boolean).join("\n\n");
    if (!context) {
      setContextError("Add the context Callsect should take into account.");
      return;
    }
    setContextEntryOpen(false);
    setContextError("");
    setVersionsOpen(false);
    setViewedVersion(null);
    setVersionPendingDelete(null);
    setRefineOpen(false);
    void generate(true, context);
  };

  const closeEmail = () => {
    setOpen(false);
    setEditing(false);
    setConfirmRegenerate(false);
    setContextEntryOpen(false);
    setContextDraft("");
    setContextError("");
    setVersionsOpen(false);
    setViewedVersion(null);
    setVersionPendingDelete(null);
    setRefineOpen(false);
  };

  const startEditing = () => {
    if (!email || generating) return;
    setSubjectDraft(email.subject);
    setBodyDraft(email.body);
    setEditing(true);
    setError("");
  };

  const cancelEditing = () => {
    setSubjectDraft(email?.subject || "");
    setBodyDraft(email?.body || "");
    setEditing(false);
    setError("");
  };

  const saveEdits = async () => {
    const subject = subjectDraft.trim();
    const body = bodyDraft.trim();
    if (!subject || !body || saving) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await api.patch(`/calls/${encodeURIComponent(callId)}/follow-up-email`, {
        subject,
        body,
      });
      const next = acceptEmail(data?.follow_up_email, data?.follow_up_email_versions);
      if (!next) throw new Error("Incomplete follow-up email response");
      setEditing(false);
      toast.success("Follow-up email saved");
    } catch (saveError) {
      setError(getActionableError(saveError, "Callsect could not save your changes."));
    } finally {
      setSaving(false);
    }
  };

  const copyEmail = async () => {
    const copySource = viewedVersion || email;
    if (!copySource) return;
    try {
      await navigator.clipboard.writeText(`Subject: ${copySource.subject}\n\n${copySource.body}`);
      toast.success("Follow-up email copied");
    } catch {
      toast.error("The email could not be copied automatically.");
    }
  };

  const restoreVersion = async () => {
    if (!callId || !viewedVersion?.version_id || restoring) return;
    setRestoring(true);
    setError("");
    try {
      const { data } = await api.post(
        `/calls/${encodeURIComponent(callId)}/follow-up-email/versions/${encodeURIComponent(viewedVersion.version_id)}/restore`
      );
      const next = acceptEmail(data?.follow_up_email, data?.follow_up_email_versions);
      if (!next) throw new Error("Incomplete follow-up email response");
      setVersionsOpen(false);
      toast.success("Follow-up email version restored");
    } catch (restoreError) {
      setError(getActionableError(restoreError, "Callsect could not restore this version."));
    } finally {
      setRestoring(false);
    }
  };

  const refineEmail = (refinement) => {
    if (!email || generating) return;
    setRefineOpen(false);
    setVersionsOpen(false);
    setViewedVersion(null);
    void generate(true, null, refinement);
  };

  const dismissSuggestion = async () => {
    if (!callId || dismissingSuggestion) return;
    setDismissingSuggestion(true);
    setError("");
    try {
      const { data } = await api.patch(
        `/calls/${encodeURIComponent(callId)}/follow-up-email/suggestion`,
        { dismissed: true },
      );
      setSuggestionDismissedLocally(true);
      setConfirmCreate(false);
      setContextEntryOpen(false);
      setContextDraft("");
      setContextError("");
      onSuggestionDismissed?.(data?.follow_up_suggestion || { recommended: false, dismissed: true });
      toast.success("Follow-up suggestion dismissed");
    } catch (dismissError) {
      setError(getActionableError(dismissError, "Callsect could not dismiss this suggestion."));
    } finally {
      setDismissingSuggestion(false);
    }
  };

  const deleteVersion = async () => {
    if (!callId || !versionPendingDelete?.version_id || deletingVersion) return;
    const versionId = versionPendingDelete.version_id;
    setDeletingVersion(true);
    setError("");
    try {
      const { data } = await api.delete(
        `/calls/${encodeURIComponent(callId)}/follow-up-email/versions/${encodeURIComponent(versionId)}`
      );
      setVersions(savedVersions(data?.follow_up_email_versions, email));
      if (viewedVersion?.version_id === versionId) setViewedVersion(null);
      setVersionPendingDelete(null);
      toast.success("Email draft deleted");
    } catch (deleteError) {
      setVersionPendingDelete(null);
      setError(getActionableError(deleteError, "Callsect could not delete this email draft."));
      setVersionsOpen(false);
    } finally {
      setDeletingVersion(false);
    }
  };

  const waiting = loading || generating || restoring || dismissingSuggestion;
  const followUpSuggested = !knownAvailable && Boolean(suggestion?.recommended) && !suggestionDismissedLocally;
  const triggerLabel = knownAvailable ? "Open follow-up" : followUpSuggested ? "Suggested" : "Create follow-up";
  const resolvedTriggerClassName = `${triggerClassName}${followUpSuggested ? " is-suggested" : ""}`;
  const savedContextLength = String(email?.private_context || "").length;
  const contextDraftLimit = Math.max(0, 4000 - savedContextLength - (savedContextLength ? 2 : 0));
  const remainingContextCharacters = Math.max(
    0,
    contextDraftLimit - contextDraft.length - (contextDraft.trim() ? 2 : 0)
  );
  const pasteContextImages = (event) => {
    const images = Array.from(event.clipboardData?.items || [])
      .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
      .map((item) => item.getAsFile()).filter(Boolean);
    if (images.length) {
      event.preventDefault();
      contextImageRef.current?.addFiles(images);
    }
  };
  const dropContextImages = (event) => {
    if (!event.dataTransfer?.files?.length) return;
    event.preventDefault();
    contextImageRef.current?.addFiles(event.dataTransfer.files);
  };
  const visibleEmail = viewedVersion || email;
  const visibleVersionNumber = viewedVersion
    ? versions.findIndex((version) => version.version_id === viewedVersion.version_id) + 1
    : 0;

  return (
    <>
      <button
        type="button"
        className={resolvedTriggerClassName}
        onClick={openEmail}
        disabled={waiting}
        aria-label={triggerLabel}
        title={triggerLabel}
        data-testid={`follow-up-email-${callId}`}
      >
        <EnvelopeSimple size={15} weight="duotone" aria-hidden="true" />
        <span>{triggerLabel}</span>
      </button>

      {confirmCreate && typeof document !== "undefined" ? createPortal(
        <div className="follow-up-email-confirm-backdrop follow-up-email-create-overlay" role="alertdialog" aria-modal="true" aria-labelledby={`follow-up-create-title-${callId}`}>
          <div className="follow-up-email-confirm">
            {followUpSuggested ? (
              <button
                type="button"
                className="follow-up-email-suggestion-close"
                aria-label="Close suggestion"
                title="Close"
                onClick={() => {
                  setConfirmCreate(false);
                  setContextEntryOpen(false);
                  setContextDraft("");
                  setContextError("");
                  setError("");
                }}
              >
                <X size={18} aria-hidden="true" />
              </button>
            ) : null}
            <span aria-hidden="true"><EnvelopeSimple size={20} weight="duotone" /></span>
            <h3 id={`follow-up-create-title-${callId}`}>
              {followUpSuggested ? "Prepare suggested follow-up?" : "Create follow-up email?"}
            </h3>
            {followUpSuggested ? (
              <div className="follow-up-email-suggestion-reason">
                <strong>Why Callsect suggests it</strong>
                <p>{suggestion?.reason || "This conversation ended with a concrete next step worth confirming."}</p>
                {suggestion?.timing ? <small>Suggested timing: {suggestion.timing}</small> : null}
              </div>
            ) : (
              <p>Callsect will generate a customer-ready email using this conversation, the relationship history, and your communication style.</p>
            )}
            <p className="follow-up-email-generation-disclosure">Uses 1 generation. Nothing will be sent automatically.</p>
            {error ? <p className="follow-up-email-suggestion-error" role="alert">{error}</p> : null}
            {!contextEntryOpen ? (
              <button
                type="button"
                className="follow-up-email-context-toggle"
                onClick={() => setContextEntryOpen(true)}
              >
                <span>
                  <Sparkle size={15} aria-hidden="true" />
                  Is there anything Callsect should know?
                </span>
                <strong>Add context</strong>
              </button>
            ) : (
              <div className="follow-up-email-context-field">
                <label htmlFor={`follow-up-email-create-context-${callId}`}>
                  What should Callsect take into account?
                </label>
                <textarea
                  id={`follow-up-email-create-context-${callId}`}
                  value={contextDraft}
                  onChange={(event) => {
                    setContextDraft(event.target.value);
                    if (contextError) setContextError("");
                  }}
                  onPaste={pasteContextImages}
                  onDragOver={(event) => {
                    if (Array.from(event.dataTransfer?.types || []).includes("Files")) event.preventDefault();
                  }}
                  onDrop={dropContextImages}
                  maxLength={contextDraftLimit}
                  rows={5}
                  placeholder="For example: I already checked the delivery date. Keep the email focused on confirming the next step."
                  autoFocus
                />
                <small>
                  Private. Used for this email only and not added to Relationship Memory.
                  <span>{contextDraft.length}/{contextDraftLimit}</span>
                </small>
                {!demoReadOnly ? (
                  <ContextScreenshotInput
                    ref={contextImageRef}
                    purpose="follow_up_email"
                    resourceId={callId}
                    guidance={contextDraft}
                    remainingCharacters={remainingContextCharacters}
                    onStateChange={setContextImageStatus}
                  />
                ) : null}
                {contextError ? <em role="alert">{contextError}</em> : null}
              </div>
            )}
            <div>
              {followUpSuggested ? (
                <button type="button" onClick={dismissSuggestion} disabled={dismissingSuggestion} className="follow-up-email-secondary">
                  {dismissingSuggestion ? "Dismissing..." : "Dismiss"}
                </button>
              ) : (
                <button type="button" onClick={() => {
                  setConfirmCreate(false);
                  setContextEntryOpen(false);
                  setContextDraft("");
                  setContextError("");
                }} className="follow-up-email-secondary">Cancel</button>
              )}
              <button type="button" onClick={confirmFirstGeneration} disabled={waiting || contextImageStatus === "analyzing"} className="follow-up-email-primary">
                {contextImageStatus === "selected" ? "Review images" : contextImageStatus === "analyzing" ? "Reading images..." : "Create follow-up"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      ) : null}

      {open && typeof document !== "undefined" ? createPortal(
        <div className="follow-up-email-overlay">
          <section
            className="follow-up-email-workspace"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`follow-up-email-title-${callId}`}
            aria-busy={waiting || saving}
          >
            <header className="follow-up-email-header">
              <div className="follow-up-email-header__identity">
                <span aria-hidden="true"><EnvelopeSimple size={24} weight="duotone" /></span>
                <div>
                  <h2 id={`follow-up-email-title-${callId}`}>Follow-up email</h2>
                  <p>{customerName ? `Ready for ${customerName}` : "Ready to copy and send"}</p>
                </div>
              </div>

              <div className="follow-up-email-header__actions">
                {email && !editing ? (
                  demoReadOnly ? (
                    <button type="button" onClick={copyEmail} disabled={waiting} className="follow-up-email-secondary">
                      <Copy size={15} /> Copy email
                    </button>
                  ) : (
                  <>
                    {versions.length > 1 ? (
                      <button
                        type="button"
                        onClick={() => setVersionsOpen(true)}
                        disabled={waiting}
                        className="follow-up-email-secondary"
                      >
                        <ClockCounterClockwise size={15} /> Versions ({versions.length})
                      </button>
                    ) : null}
                    {viewedVersion ? (
                      <>
                        <button type="button" onClick={copyEmail} disabled={waiting} className="follow-up-email-secondary">
                          <Copy size={15} /> Copy email
                        </button>
                        <button type="button" onClick={() => setViewedVersion(null)} disabled={waiting} className="follow-up-email-secondary">
                          Back to current
                        </button>
                        <button type="button" onClick={restoreVersion} disabled={waiting} className="follow-up-email-primary">
                          <ClockCounterClockwise size={15} /> {restoring ? "Restoring..." : "Restore version"}
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setContextDraft("");
                            setContextError("");
                            setContextEntryOpen(true);
                          }}
                          disabled={waiting || contextDraftLimit === 0}
                          title={contextDraftLimit === 0 ? "The private context for this email has reached its limit" : "Add context"}
                          className="follow-up-email-secondary"
                        >
                          <Sparkle size={15} aria-hidden="true" /> Add context
                        </button>
                        <button type="button" onClick={copyEmail} disabled={waiting} className="follow-up-email-secondary">
                          <Copy size={15} /> Copy email
                        </button>
                        <button type="button" onClick={startEditing} disabled={waiting} className="follow-up-email-secondary">
                          <PencilSimple size={15} /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setRefineOpen(true)}
                          disabled={waiting}
                          className="follow-up-email-secondary"
                        >
                          <MagicWand size={15} /> Refine
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRefineOpen(false);
                            setConfirmRegenerate(true);
                          }}
                          disabled={waiting}
                          className="follow-up-email-primary"
                        >
                          <ArrowClockwise size={15} /> Regenerate
                        </button>
                      </>
                    )}
                  </>
                  )
                ) : null}
                <button type="button" onClick={closeEmail} className="follow-up-email-close" aria-label="Close follow-up email">
                  <X size={19} />
                </button>
              </div>
            </header>

            <main className="follow-up-email-content">
              {waiting && !email ? (
                <div className="follow-up-email-loading" role="status" aria-live="polite">
                  <WorkingHourglass size={28} />
                  <strong>{loading ? "Opening your follow-up email..." : "Writing your follow-up email..."}</strong>
                  <p>Callsect is turning this conversation and relationship history into a customer-ready message.</p>
                </div>
              ) : error && !email ? (
                <div className="follow-up-email-failure" role="alert">
                  <WarningCircle size={30} weight="duotone" />
                  <strong>The follow-up email could not be created</strong>
                  <p>{error}</p>
                  <div>
                    <button type="button" onClick={() => generate(false, contextDraft.trim() || null)} className="follow-up-email-primary">
                      Try again
                    </button>
                    <button type="button" onClick={closeEmail} className="follow-up-email-secondary">Close</button>
                  </div>
                </div>
              ) : email ? (
                <div className="follow-up-email-sheet">
                  {error ? (
                    <div className="follow-up-email-inline-error" role="alert">
                      <WarningCircle size={18} weight="duotone" />
                      <span>{error}</span>
                      {contextDraft.trim() ? (
                        <button type="button" onClick={() => generate(true, contextDraft.trim())} className="follow-up-email-inline-retry">
                          Try again
                        </button>
                      ) : null}
                      <button type="button" onClick={() => setError("")} aria-label="Dismiss error"><X size={14} /></button>
                    </div>
                  ) : null}

                  {generating ? (
                    <div className="follow-up-email-progress" role="status" aria-live="polite">
                      <WorkingHourglass size={17} />
                      {activeRefinement
                        ? `Creating a ${refinementLabel(activeRefinement).toLowerCase()} version. `
                        : "Creating a new version. "}
                      Your current email will stay available if this fails.
                    </div>
                  ) : null}

                  {viewedVersion ? (
                    <div className="follow-up-email-version-preview" role="status">
                      <span>
                        Viewing Version {visibleVersionNumber}: {versionKindLabel(viewedVersion)}
                      </span>
                      <small>{versionTime(viewedVersion)}</small>
                      <button type="button" onClick={restoreVersion} disabled={restoring}>
                        {restoring ? "Restoring..." : "Restore without using AI"}
                      </button>
                    </div>
                  ) : null}

                  <label className="follow-up-email-subject">
                    <span>Subject</span>
                    {editing ? (
                      <input
                        value={subjectDraft}
                        onChange={(event) => setSubjectDraft(event.target.value)}
                        maxLength={300}
                        disabled={saving}
                      />
                    ) : (
                      <strong>{visibleEmail.subject}</strong>
                    )}
                  </label>

                  <div className="follow-up-email-body">
                    {editing ? (
                      <textarea
                        value={bodyDraft}
                        onChange={(event) => setBodyDraft(event.target.value)}
                        maxLength={12000}
                        disabled={saving}
                        aria-label="Follow-up email text"
                      />
                    ) : (
                      <div>{visibleEmail.body}</div>
                    )}
                  </div>

                  {editing ? (
                    <div className="follow-up-email-edit-actions">
                      <button type="button" onClick={cancelEditing} disabled={saving} className="follow-up-email-secondary">
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={saveEdits}
                        disabled={saving || !subjectDraft.trim() || !bodyDraft.trim()}
                        className="follow-up-email-primary"
                      >
                        {saving ? "Saving..." : "Save changes"}
                      </button>
                    </div>
                  ) : null}

                  <small className="follow-up-email-source-note">
                    {demoReadOnly
                      ? "Demo example built from this conversation, the relationship history, and the sample writing profile. Nothing is sent automatically."
                      : `Built from this conversation, the relationship history, your communication style${visibleEmail.private_context ? ", and the private context you added" : ""}. Review before sending.`}
                  </small>
                </div>
              ) : null}
            </main>

            {refineOpen ? (
              <div className="follow-up-email-confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="follow-up-refine-title">
                <div className="follow-up-email-confirm follow-up-email-refine-dialog">
                  <span aria-hidden="true"><MagicWand size={20} /></span>
                  <h3 id="follow-up-refine-title">Refine this email</h3>
                  <p>Choose how to reshape the current draft. Each refinement uses 1 generation and is saved as a new version.</p>
                  <div className="follow-up-email-refine-options">
                    {REFINEMENT_OPTIONS.map((option) => (
                      <button
                        type="button"
                        key={option.value}
                        onClick={() => refineEmail(option.value)}
                      >
                        <strong>{option.label}</strong>
                        <span>{option.description}</span>
                      </button>
                    ))}
                  </div>
                  <div>
                    <button type="button" onClick={() => setRefineOpen(false)} className="follow-up-email-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            ) : null}

            {confirmRegenerate ? (
              <div className="follow-up-email-confirm-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="follow-up-regenerate-title">
                <div className="follow-up-email-confirm">
                  <span aria-hidden="true"><ArrowClockwise size={20} /></span>
                  <h3 id="follow-up-regenerate-title">Regenerate this email?</h3>
                  <p>This uses another AI generation. Your current email will remain available if the new version cannot be created.</p>
                  <div>
                    <button type="button" onClick={() => setConfirmRegenerate(false)} className="follow-up-email-secondary">Cancel</button>
                    <button
                      type="button"
                      onClick={() => {
                        setConfirmRegenerate(false);
                        void generate(true);
                      }}
                      className="follow-up-email-primary"
                    >
                      Regenerate
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {versionsOpen ? (
              <div className="follow-up-email-confirm-backdrop" role="dialog" aria-modal="true" aria-labelledby="follow-up-versions-title">
                <div className="follow-up-email-confirm follow-up-email-versions-dialog">
                  <span aria-hidden="true"><ClockCounterClockwise size={20} /></span>
                  <h3 id="follow-up-versions-title">Email versions</h3>
                  <p>Open any saved draft. Restoring an earlier version does not use AI or consume a generation.</p>
                  <div className="follow-up-email-version-list">
                    {[...versions].reverse().map((version, reverseIndex) => {
                      const versionNumber = versions.length - reverseIndex;
                      const isCurrent = version.version_id === email.version_id;
                      const isViewed = version.version_id === viewedVersion?.version_id;
                      return (
                        <article
                          className={`follow-up-email-version-item${isCurrent ? " is-current" : ""}${isViewed ? " is-viewed" : ""}`}
                          key={version.version_id || `${versionNumber}-${version.updated_at}`}
                        >
                          <div className="follow-up-email-version-meta">
                            <strong>Version {versionNumber}</strong>
                            <span>{versionKindLabel(version)}</span>
                            {isCurrent ? <em>Current</em> : null}
                          </div>
                          <p>{version.subject}</p>
                          <small>{versionTime(version)}</small>
                          <div className="follow-up-email-version-actions">
                            <button
                              type="button"
                              onClick={() => {
                                setViewedVersion(isCurrent ? null : version);
                                setVersionsOpen(false);
                                setError("");
                              }}
                              className="follow-up-email-secondary"
                            >
                              {isCurrent ? "View current" : "View"}
                            </button>
                            {!isCurrent ? (
                              <button
                                type="button"
                                onClick={() => setVersionPendingDelete({ ...version, versionNumber })}
                                className="follow-up-email-version-delete"
                                aria-label={`Delete Version ${versionNumber}`}
                                title={`Delete Version ${versionNumber}`}
                              >
                                <Trash size={15} aria-hidden="true" />
                                Delete
                              </button>
                            ) : null}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                  <div>
                    <button type="button" onClick={() => setVersionsOpen(false)} className="follow-up-email-secondary">Close</button>
                  </div>
                </div>
              </div>
            ) : null}

            {versionPendingDelete ? (
              <div className="follow-up-email-confirm-backdrop follow-up-email-delete-version-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="follow-up-delete-version-title">
                <div className="follow-up-email-confirm">
                  <span className="follow-up-email-delete-version-icon" aria-hidden="true"><Trash size={20} /></span>
                  <h3 id="follow-up-delete-version-title">Delete this email draft?</h3>
                  <p>
                    Version {versionPendingDelete.versionNumber} will be permanently removed. This does not use AI or consume a generation.
                  </p>
                  <div>
                    <button
                      type="button"
                      onClick={() => setVersionPendingDelete(null)}
                      disabled={deletingVersion}
                      className="follow-up-email-secondary"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={deleteVersion}
                      disabled={deletingVersion}
                      className="follow-up-email-danger"
                    >
                      {deletingVersion ? "Deleting..." : "Delete draft"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {contextEntryOpen && email ? (
              <div className="follow-up-email-confirm-backdrop" role="alertdialog" aria-modal="true" aria-labelledby="follow-up-add-context-title">
                <div className="follow-up-email-confirm follow-up-email-context-dialog">
                  <span aria-hidden="true"><EnvelopeSimple size={20} weight="duotone" /></span>
                  <h3 id="follow-up-add-context-title">Add context</h3>
                  <p>
                    {email.private_context
                      ? "Callsect will build on the private context already used for this email. Your new note will be added to it, not replace it."
                      : "Add something you know after the call that Callsect should use when rewriting this email."}
                  </p>
                  <div className="follow-up-email-context-field">
                    <label htmlFor={`follow-up-email-context-${callId}`}>What should Callsect take into account?</label>
                    <textarea
                      id={`follow-up-email-context-${callId}`}
                      value={contextDraft}
                      onChange={(event) => {
                        setContextDraft(event.target.value);
                        if (contextError) setContextError("");
                      }}
                      onPaste={pasteContextImages}
                      onDragOver={(event) => {
                        if (Array.from(event.dataTransfer?.types || []).includes("Files")) event.preventDefault();
                      }}
                      onDrop={dropContextImages}
                      maxLength={contextDraftLimit}
                      rows={6}
                      placeholder="For example: Do not revisit the earlier pricing objection. Focus on the delivery date we have now confirmed."
                      autoFocus
                    />
                    <small>
                      Private. Used for this email only and not added to Relationship Memory.
                      <span>{contextDraft.length}/{contextDraftLimit}</span>
                    </small>
                    {!demoReadOnly ? (
                      <ContextScreenshotInput
                        ref={contextImageRef}
                        purpose="follow_up_email"
                        resourceId={callId}
                        guidance={contextDraft}
                        remainingCharacters={remainingContextCharacters}
                        onStateChange={setContextImageStatus}
                      />
                    ) : null}
                    {contextError ? <em role="alert">{contextError}</em> : null}
                  </div>
                  <div>
                    <button type="button" onClick={() => {
                      setContextEntryOpen(false);
                      setContextDraft("");
                      setContextError("");
                    }} className="follow-up-email-secondary">Cancel</button>
                    <button type="button" onClick={addContext} disabled={(!contextDraft.trim() && contextImageStatus === "empty") || contextImageStatus === "analyzing"} className="follow-up-email-primary">
                      {contextImageStatus === "selected" ? "Review images" : contextImageStatus === "analyzing" ? "Reading images..." : "Add context"}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        </div>,
        document.body
      ) : null}
    </>
  );
}
